import { fetchApi } from "@/api/client";
import type { SensorRecord } from "@/api/types";

const PLOT_PAGE = 1000;
export const MAX_RECORDS = 10000;
export const RECORD_LIMIT_OPTIONS = [2000, 10000, 25000, 50000] as const;
const MAX_DATE_SLICES = 31;
const SLICE_CONCURRENCY = 6;
const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface PlotFilters {
  source?: string;
  user_id?: string;
  device_id?: string;
  from?: string;
  to?: string;
  maxRecords?: number;
}

export interface PlotFetchResult {
  records: SensorRecord[];
  truncated: boolean;
  maxRecords: number;
  totalSlices: number;
  completedSlices: number;
}

function splitDateRangeIntoSlices(
  from?: string,
  to?: string,
): Array<{ from?: string; to?: string }> {
  if (!from || !ISO_DATE_RE.test(from)) {
    return [{ from, to }];
  }
  const effectiveTo =
    to && ISO_DATE_RE.test(to) ? to : new Date().toISOString().slice(0, 10);
  const startMs = Date.parse(`${from}T00:00:00Z`);
  const endMs = Date.parse(`${effectiveTo}T00:00:00Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return [{ from, to }];
  }

  const loMs = Math.min(startMs, endMs);
  const hiMs = Math.max(startMs, endMs);
  const totalDays = Math.floor((hiMs - loMs) / DAY_MS) + 1;
  if (totalDays <= 1) {
    const singleDay = new Date(loMs).toISOString().slice(0, 10);
    return [{ from: singleDay, to: singleDay }];
  }

  const numSlices = Math.min(totalDays, MAX_DATE_SLICES);
  const slices: Array<{ from: string; to: string }> = [];
  for (let i = 0; i < numSlices; i++) {
    const startIdx = Math.floor((i * totalDays) / numSlices);
    const endIdx = Math.floor(((i + 1) * totalDays) / numSlices) - 1;
    slices.push({
      from: new Date(loMs + startIdx * DAY_MS).toISOString().slice(0, 10),
      to: new Date(loMs + endIdx * DAY_MS).toISOString().slice(0, 10),
    });
  }
  return slices.reverse();
}

async function fetchSourceRecordsWithMeta(
  f: PlotFilters,
  source?: string,
): Promise<PlotFetchResult> {
  const maxRecords =
    f.maxRecords && f.maxRecords > 0 ? f.maxRecords : MAX_RECORDS;
  const slices = splitDateRangeIntoSlices(f.from, f.to);

  const states = slices.map((slice) => ({
    from: slice.from,
    to: slice.to,
    cursor: undefined as string | undefined,
    done: false,
    records: [] as SensorRecord[],
  }));

  let totalFetched = 0;

  while (totalFetched < maxRecords) {
    const active = states.filter((s) => !s.done);
    if (active.length === 0) break;

    const remainingBudget = maxRecords - totalFetched;
    const perSliceLimit = Math.min(
      PLOT_PAGE,
      Math.max(1, Math.ceil(remainingBudget / active.length)),
    );

    for (let i = 0; i < active.length; i += SLICE_CONCURRENCY) {
      if (totalFetched >= maxRecords) break;
      const batch = active.slice(i, i + SLICE_CONCURRENCY);
      await Promise.all(
        batch.map(async (sliceState) => {
          const res = await fetchApi<SensorRecord[]>("/sensors", {
            source,
            user_id: f.user_id,
            device_id: f.device_id,
            from: sliceState.from,
            to: sliceState.to,
            limit: String(perSliceLimit),
            cursor: sliceState.cursor,
          });
          sliceState.records.push(...res.data);
          totalFetched += res.data.length;
          sliceState.cursor = res.next_cursor ?? undefined;
          if (!sliceState.cursor || res.data.length === 0) {
            sliceState.done = true;
          }
        }),
      );
    }
  }

  const seen = new Set<string>();
  const merged: SensorRecord[] = [];
  for (const state of states) {
    for (const r of state.records) {
      const key = `${r.participant_id}|${r.collected_at}|${r.source}|${r.uuid ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(r);
    }
  }
  merged.sort((a, b) => b.collected_at.localeCompare(a.collected_at));

  const completedSlices = states.filter((s) => s.done).length;
  const truncated = states.some((s) => !s.done);

  return {
    records: merged,
    truncated,
    maxRecords,
    totalSlices: states.length,
    completedSlices,
  };
}

export async function fetchPlotRecordsWithMeta(
  f: PlotFilters,
): Promise<PlotFetchResult> {
  if (f.source?.toUpperCase() === "SLEEP") {
    const [sleepRes, hcRes] = await Promise.all([
      fetchSourceRecordsWithMeta(f, "SLEEP"),
      fetchSourceRecordsWithMeta(f, "HEALTH_CONNECT").catch(
        (): PlotFetchResult | null => null,
      ),
    ]);
    const hcWithSleep = (hcRes?.records ?? []).filter((r) => {
      const s = r.values?.sleep_sessions;
      return (
        (Array.isArray(s) && s.length > 0) ||
        (typeof s === "string" && s.trim().startsWith("["))
      );
    });
    return {
      records: [...sleepRes.records, ...hcWithSleep],
      truncated: sleepRes.truncated || Boolean(hcRes?.truncated),
      maxRecords: sleepRes.maxRecords,
      totalSlices: Math.max(sleepRes.totalSlices, hcRes?.totalSlices ?? 0),
      completedSlices: hcRes
        ? Math.min(sleepRes.completedSlices, hcRes.completedSlices)
        : sleepRes.completedSlices,
    };
  }
  return fetchSourceRecordsWithMeta(f, f.source);
}

export async function fetchPlotRecords(f: PlotFilters): Promise<SensorRecord[]> {
  const res = await fetchPlotRecordsWithMeta(f);
  return res.records;
}

export function rangeLabel(f: PlotFilters, usingDefault: boolean): string {
  if (usingDefault) return "last 24 hours";
  if (f.from && f.to) return `${f.from} → ${f.to}`;
  if (f.from) return `since ${f.from}`;
  if (f.to) return `until ${f.to}`;
  return "all time";
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  yAxisId?: "left" | "right";
  stackId?: string;
  strokeWidth?: number;
  fillOpacity?: number;
}

export interface ChartReferenceLine {
  y: number;
  label?: string;
  color?: string;
}

export interface ChartTooltipMeta {
  key: string;
  label: string;
  format?: ChartValueFormat;
}

export type ChartValueFormat =
  | "number"
  | "hourOfDay"
  | "coordinate"
  | "score"
  | "seconds"
  | "minutes"
  | "hours";

export interface LocationPoint {
  lat: number;
  lng: number;
  label: string;
  accuracy: number | null;
  timeOfDay: string | null;
}

export interface ChartSpec {
  id: string;
  type: "bar" | "horizontal-bar" | "line" | "area" | "map";
  title: string;
  subtitle?: string;
  data: Record<string, string | number>[];
  /** GPS points for map/heatmap charts */
  points?: LocationPoint[];
  /** Defaults to [{ key: "value", label: "Value", color: "#2563EB" }] when omitted */
  series?: ChartSeries[];
  /** rotate x-axis labels for long category names */
  rotateLabels?: boolean;
  /** render visible dots on line charts */
  showDots?: boolean;
  /** custom y-axis domain, e.g. ["auto", "auto"] for coordinates or [0, 24] for hour of day */
  yDomain?: [number | "auto" | "dataMin" | "dataMax", number | "auto" | "dataMin" | "dataMax"];
  yRightDomain?: [number | "auto" | "dataMin" | "dataMax", number | "auto" | "dataMin" | "dataMax"];
  xAxisLabel?: string;
  yAxisLabel?: string;
  yRightAxisLabel?: string;
  valueFormat?: ChartValueFormat;
  referenceLines?: ChartReferenceLine[];
  tooltipMeta?: ChartTooltipMeta[];
}

const HOURLY_SPAN_MS = 48 * 60 * 60 * 1000;
const MAX_CATEGORIES = 12;

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function parseNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v.trim());
    return v.trim() !== "" && Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseJsonArray(v: unknown): unknown[] | null {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function round5(n: number): number {
  return Math.round(n * 100000) / 100000;
}

function recordTimestampMs(r: SensorRecord, valueKeys: string[] = []): number | null {
  for (const k of valueKeys) {
    const raw = r.values?.[k];
    if (raw === null || raw === undefined || raw === "") continue;
    const asNum = parseNum(raw);
    if (asNum !== null && asNum > 1_000_000_000_000) return asNum;
    if (typeof raw === "string") {
      const parsed = Date.parse(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  const collected = Date.parse(r.collected_at);
  return Number.isFinite(collected) ? collected : null;
}

function formatTimestampLabel(ms: number): string {
  const d = new Date(ms);
  const M = d.getUTCMonth() + 1;
  const D = d.getUTCDate();
  const H = pad2(d.getUTCHours());
  const Min = pad2(d.getUTCMinutes());
  return `${M}/${D} ${H}:${Min}`;
}

// Buckets use UTC so charts line up with the UTC date filters and the
// "since YYYY-MM-DD" range label (local buckets shifted boundary records
// onto the day before the filter start).
function timeBucket(d: Date, hourly: boolean): { key: string; label: string } {
  const M = d.getUTCMonth() + 1;
  const D = d.getUTCDate();
  if (hourly) {
    const H = d.getUTCHours();
    return {
      key: `${d.getUTCFullYear()}${pad2(M)}${pad2(D)}${pad2(H)}`,
      label: `${M}/${D} ${pad2(H)}:00`,
    };
  }
  return { key: `${d.getUTCFullYear()}${pad2(M)}${pad2(D)}`, label: `${M}/${D}` };
}

export interface TimeWindow {
  from?: string;
  to?: string;
}

/** granularity follows the requested window, falling back to the data span */
function isHourly(records: SensorRecord[], window?: TimeWindow): boolean {
  if (window?.from) {
    const start = new Date(window.from).getTime();
    const end = window.to ? new Date(window.to).getTime() : Date.now();
    if (Number.isFinite(start) && Number.isFinite(end)) return end - start <= HOURLY_SPAN_MS;
  }
  const times = records.map((r) => new Date(r.collected_at).getTime()).filter(Number.isFinite);
  if (times.length < 2) return true;
  return Math.max(...times) - Math.min(...times) <= HOURLY_SPAN_MS;
}

/** count of records per time bucket (zero-filling empty intermediate buckets so gaps are visible) */
function recordsOverTime(
  records: SensorRecord[],
  hourly: boolean,
  window?: TimeWindow,
): ChartSpec {
  const buckets = new Map<string, { label: string; value: number }>();
  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const r of records) {
    const d = new Date(r.collected_at);
    const ms = d.getTime();
    if (Number.isNaN(ms)) continue;
    if (ms < minMs) minMs = ms;
    if (ms > maxMs) maxMs = ms;
    const { key, label } = timeBucket(d, hourly);
    const b = buckets.get(key) ?? { label, value: 0 };
    b.value += 1;
    buckets.set(key, b);
  }
  if (window?.from) {
    const wStart = ISO_DATE_RE.test(window.from)
      ? Date.parse(`${window.from}T00:00:00Z`)
      : Date.parse(window.from);
    if (Number.isFinite(wStart) && wStart < minMs) {
      minMs = wStart;
    }
  }
  if (window?.to) {
    const wEnd = ISO_DATE_RE.test(window.to)
      ? Date.parse(`${window.to}T23:59:59Z`)
      : Date.parse(window.to);
    if (Number.isFinite(wEnd) && wEnd > maxMs) {
      maxMs = wEnd;
    }
  } else if (window?.from && Number.isFinite(minMs)) {
    const nowMs = Date.now();
    if (nowMs > maxMs) maxMs = nowMs;
  }
  if (Number.isFinite(minMs) && Number.isFinite(maxMs) && maxMs >= minMs) {
    const minD = new Date(minMs);
    const maxD = new Date(maxMs);
    const stepMs = hourly ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    const startCursor = hourly
      ? Date.UTC(minD.getUTCFullYear(), minD.getUTCMonth(), minD.getUTCDate(), minD.getUTCHours())
      : Date.UTC(minD.getUTCFullYear(), minD.getUTCMonth(), minD.getUTCDate());
    const endCursor = hourly
      ? Date.UTC(maxD.getUTCFullYear(), maxD.getUTCMonth(), maxD.getUTCDate(), maxD.getUTCHours())
      : Date.UTC(maxD.getUTCFullYear(), maxD.getUTCMonth(), maxD.getUTCDate());
    const maxSteps = 366;
    if ((endCursor - startCursor) / stepMs <= maxSteps) {
      for (let t = startCursor; t <= endCursor; t += stepMs) {
        const { key, label } = timeBucket(new Date(t), hourly);
        if (!buckets.has(key)) {
          buckets.set(key, { label, value: 0 });
        }
      }
    }
  }
  const data = [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  return {
    id: "over-time",
    type: "bar",
    title: `Records over time (${hourly ? "hourly" : "daily"}, UTC)`,
    xAxisLabel: "Time (UTC)",
    yAxisLabel: "Records",
    series: [{ key: "value", label: "Records", color: "#64748B" }],
    data,
  };
}

/** aggregate a numeric field per time bucket */
function metricOverTime(
  records: SensorRecord[],
  field: string,
  agg: "avg" | "sum",
  title: string,
  hourly: boolean,
  transform?: (n: number) => number,
): ChartSpec | null {
  const buckets = new Map<string, { label: string; sum: number; count: number }>();
  for (const r of records) {
    let n = parseNum(r.values?.[field]);
    if (n === null) continue;
    if (transform) n = transform(n);
    const d = new Date(r.collected_at);
    if (Number.isNaN(d.getTime())) continue;
    const { key, label } = timeBucket(d, hourly);
    const b = buckets.get(key) ?? { label, sum: 0, count: 0 };
    b.sum += n;
    b.count += 1;
    buckets.set(key, b);
  }
  if (buckets.size === 0) return null;
  const data = [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => ({ label: v.label, value: round(agg === "avg" ? v.sum / v.count : v.sum) }));
  return { id: `metric-${field}`, type: agg === "avg" ? "line" : "bar", title, data };
}

/** frequency of each value of a categorical field */
function categoryCounts(records: SensorRecord[], field: string, title: string): ChartSpec | null {
  const counts = new Map<string, number>();
  for (const r of records) {
    const v = r.values?.[field];
    if (v === null || v === undefined || v === "") continue;
    const key = typeof v === "boolean" ? (v ? "true" : "false") : String(v);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  const data = [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, MAX_CATEGORIES)
    .map(([label, value]) => ({ label, value }));
  const maxLabelLen = Math.max(0, ...data.map((d) => d.label.length));
  const useHorizontal = data.length > 4 || maxLabelLen > 10;
  return {
    id: `cat-${field}`,
    type: useHorizontal ? "horizontal-bar" : "bar",
    title,
    xAxisLabel: useHorizontal ? "Records" : undefined,
    yAxisLabel: useHorizontal ? undefined : "Records",
    data,
    rotateLabels: !useHorizontal,
  };
}

/** sum several numeric fields into one bar each */
function multiField(
  records: SensorRecord[],
  fields: { key: string; label: string }[],
  title: string,
): ChartSpec | null {
  const data = fields.map(({ key, label }) => {
    let total = 0;
    for (const r of records) {
      const n = parseNum(r.values?.[key]);
      if (n !== null) total += n;
    }
    return { label, value: round(total) };
  });
  if (data.every((d) => d.value === 0)) return null;
  return { id: `multi-${fields[0]?.key}`, type: "bar", title, data };
}

// ── Specialized source visualizations ────────────────────────────────────────

/**
 * Ambient Audio: show label and score.
 * Extracts `{ label, score }` entries from `top_labels` (or direct label/score fields),
 * selects the top acoustic labels by detection frequency, and plots average confidence
 * score per label on a horizontal bar chart alongside acoustic scene categories.
 */
function ambientAudioCharts(records: SensorRecord[]): ChartSpec[] {
  const byLabel = new Map<string, { sum: number; count: number }>();

  for (const r of records) {
    const topLabels = parseJsonArray(r.values?.top_labels);
    if (topLabels && topLabels.length > 0) {
      for (const entry of topLabels) {
        if (!entry || typeof entry !== "object") continue;
        const obj = entry as Record<string, unknown>;
        const label = String(obj.label ?? "").trim();
        const score = parseNum(obj.score);
        if (!label || score === null) continue;
        const cur = byLabel.get(label) ?? { sum: 0, count: 0 };
        cur.sum += score;
        cur.count += 1;
        byLabel.set(label, cur);
      }
      continue;
    }

    const directLabel = String(
      r.values?.label ?? r.values?.category ?? r.values?.classification ?? "",
    ).trim();
    const directScore = parseNum(
      r.values?.score ?? r.values?.confidence ?? r.values?.speech_ratio,
    );
    if (directLabel && directScore !== null) {
      const cur = byLabel.get(directLabel) ?? { sum: 0, count: 0 };
      cur.sum += directScore;
      cur.count += 1;
      byLabel.set(directLabel, cur);
    }
  }

  const out: ChartSpec[] = [];
  if (byLabel.size > 0) {
    const data = [...byLabel.entries()]
      .map(([label, { sum, count }]) => ({
        label,
        value: round3(sum / count),
        detections: count,
      }))
      // Take the most frequent acoustic labels so single-sample flukes don't crowd out real sounds,
      // then order by average score descending.
      .sort((a, b) => b.detections - a.detections || b.value - a.value)
      .slice(0, MAX_CATEGORIES)
      .sort((a, b) => b.value - a.value || b.detections - a.detections);

    out.push({
      id: "ambient-audio-label-score",
      type: "horizontal-bar",
      title: "Ambient audio labels by confidence score (avg)",
      xAxisLabel: "Avg confidence score (0–1)",
      yAxisLabel: "Label",
      yDomain: [0, 1],
      valueFormat: "score",
      series: [{ key: "value", label: "Avg score", color: "#0284C7" }],
      tooltipMeta: [{ key: "detections", label: "Detections" }],
      data,
    });
  }

  const cat =
    categoryCounts(records, "category", "Ambient audio scene categories") ??
    categoryCounts(records, "classification", "Audio classification");
  if (cat) {
    out.push({
      ...cat,
      series: [{ key: "value", label: "Samples", color: "#0D9488" }],
    });
  }

  return out;
}

/**
 * App Usage: show top apps.
 * Aggregates usage duration (`interactive_seconds` / `visible_seconds` / `foreground_seconds`)
 * by `app_name` (falling back to `package_name`) into a horizontal leaderboard chart,
 * plus an app category breakdown when `app_category` is available.
 */
function appUsageCharts(records: SensorRecord[]): ChartSpec[] {
  const byApp = new Map<
    string,
    { interactiveSec: number; passiveSec: number; totalSec: number; count: number }
  >();
  const byCategory = new Map<string, number>();

  for (const r of records) {
    const rawName = r.values?.app_name || r.values?.package_name;
    if (rawName === null || rawName === undefined || String(rawName).trim() === "") continue;
    const appName = String(rawName).trim();
    const interactive =
      parseNum(r.values?.interactive_seconds) ??
      parseNum(r.values?.foreground_seconds) ??
      null;
    const visible = parseNum(r.values?.visible_seconds);
    const activeSec = Math.max(0, interactive ?? visible ?? 0);
    const passiveSec =
      interactive !== null && visible !== null && visible > interactive
        ? Math.max(0, visible - interactive)
        : 0;
    const totalSec = activeSec + passiveSec;

    const cur = byApp.get(appName) ?? {
      interactiveSec: 0,
      passiveSec: 0,
      totalSec: 0,
      count: 0,
    };
    cur.interactiveSec += activeSec;
    cur.passiveSec += passiveSec;
    cur.totalSec += totalSec;
    cur.count += 1;
    byApp.set(appName, cur);

    const rawCat = r.values?.app_category;
    if (typeof rawCat === "string" && rawCat.trim() !== "" && totalSec > 0) {
      const catKey = rawCat.trim().replace(/_/g, " ");
      byCategory.set(catKey, (byCategory.get(catKey) ?? 0) + totalSec);
    }
  }

  if (byApp.size === 0) return [];

  const hasDuration = [...byApp.values()].some((v) => v.totalSec > 0);
  const hasPassive = hasDuration && [...byApp.values()].some((v) => v.passiveSec >= 30);

  const topEntries = [...byApp.entries()]
    .map(([label, v]) => {
      const interactiveMin = round(v.interactiveSec / 60);
      const passiveMin = round(v.passiveSec / 60);
      const totalMin = round(v.totalSec / 60);
      return {
        label,
        value: hasDuration ? totalMin : v.count,
        interactive: interactiveMin,
        passive: passiveMin,
      };
    })
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, MAX_CATEGORIES);

  if (topEntries.length === 0) return [];

  const out: ChartSpec[] = [
    {
      id: "app-usage-top-apps",
      type: "horizontal-bar",
      title: hasDuration ? "Top apps by usage (minutes)" : "Top apps by activity count",
      xAxisLabel: hasDuration ? "Minutes" : "Records",
      yAxisLabel: "App",
      valueFormat: hasDuration ? "minutes" : "number",
      series: hasPassive
        ? [
            {
              key: "interactive",
              label: "Active (interactive)",
              color: "#2563EB",
              stackId: "usage",
            },
            {
              key: "passive",
              label: "Passive (visible)",
              color: "#93C5FD",
              stackId: "usage",
            },
          ]
        : [
            {
              key: "value",
              label: hasDuration ? "Usage" : "Records",
              color: "#2563EB",
            },
          ],
      data: topEntries,
    },
  ];

  if (byCategory.size > 1) {
    const catData = [...byCategory.entries()]
      .map(([label, sec]) => ({ label, value: round(sec / 60) }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_CATEGORIES);

    if (catData.length > 0) {
      out.push({
        id: "app-usage-by-category",
        type: "horizontal-bar",
        title: "Usage by app category (minutes)",
        xAxisLabel: "Minutes",
        yAxisLabel: "Category",
        valueFormat: "minutes",
        series: [{ key: "value", label: "Usage", color: "#6366F1" }],
        data: catData,
      });
    }
  }

  return out;
}

/**
 * Location (`AT_LOCATION`): show heatmap on interactive map.
 */
function locationCharts(records: SensorRecord[]): ChartSpec[] {
  const rawPoints: {
    ms: number;
    lat: number;
    lng: number;
    accuracy: number | null;
    timeOfDay: string | null;
  }[] = [];

  for (const r of records) {
    const lat = parseNum(r.values?.latitude);
    const lng = parseNum(r.values?.longitude);
    const ms = recordTimestampMs(r, ["timestamp", "time"]);
    if (lat === null || lng === null || ms === null) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    const accuracy = parseNum(r.values?.accuracy);
    const timeOfDay =
      typeof r.values?.time_of_day === "string" && r.values.time_of_day.trim() !== ""
        ? r.values.time_of_day.trim()
        : null;
    rawPoints.push({ ms, lat, lng, accuracy, timeOfDay });
  }

  if (rawPoints.length === 0) {
    const fallback = categoryCounts(records, "cluster_id", "Location cluster visits");
    return fallback ? [fallback] : [];
  }

  rawPoints.sort((a, b) => a.ms - b.ms);

  const mapPoints: LocationPoint[] = rawPoints.map((p) => ({
    lat: round5(p.lat),
    lng: round5(p.lng),
    label: formatTimestampLabel(p.ms),
    accuracy: p.accuracy,
    timeOfDay: p.timeOfDay,
  }));

  return [
    {
      id: "location-heatmap",
      type: "map",
      title: "Location heatmap & trajectory",
      data: [],
      points: mapPoints,
    },
  ];
}

/**
 * Bluetooth (`BLUETOOTH_SCAN`): show device count.
 * Uses a shaded area chart and splits into RSSI proximity tiers when `devices[].level` is present.
 */
function bluetoothCharts(records: SensorRecord[], hourly: boolean): ChartSpec[] {
  const points: {
    ms: number;
    count: number;
    close: number;
    medium: number;
    distant: number;
    hasRssi: boolean;
  }[] = [];

  for (const r of records) {
    const devicesArr = parseJsonArray(r.values?.devices);
    const count =
      parseNum(r.values?.count) ??
      parseNum(r.values?.bluetooth_count) ??
      (devicesArr ? devicesArr.length : null);
    const ms = recordTimestampMs(r, ["time", "timestamp"]);
    if (count === null || ms === null) continue;

    let close = 0;
    let medium = 0;
    let distant = 0;
    let rssiMeasured = 0;

    if (devicesArr && devicesArr.length > 0) {
      for (const dev of devicesArr) {
        if (!dev || typeof dev !== "object") continue;
        const lvl = parseNum((dev as Record<string, unknown>).level ?? (dev as Record<string, unknown>).rssi);
        if (lvl === null) continue;
        rssiMeasured += 1;
        if (lvl >= -65) close += 1;
        else if (lvl >= -80) medium += 1;
        else distant += 1;
      }
    }

    points.push({
      ms,
      count,
      close,
      medium,
      distant,
      hasRssi: rssiMeasured > 0,
    });
  }

  if (points.length === 0) return [];
  points.sort((a, b) => a.ms - b.ms);

  const anyRssi = points.some((p) => p.hasRssi);
  const rssiSeries: ChartSeries[] = [
    { key: "close", label: "Close (≥ -65 dBm)", color: "#2563EB", stackId: "bt", fillOpacity: 0.45 },
    { key: "medium", label: "Medium (-65 to -80 dBm)", color: "#06B6D4", stackId: "bt", fillOpacity: 0.35 },
    { key: "distant", label: "Distant (< -80 dBm)", color: "#94A3B8", stackId: "bt", fillOpacity: 0.25 },
  ];
  const singleSeries: ChartSeries[] = [
    { key: "value", label: "Device count", color: "#0284C7", fillOpacity: 0.25 },
  ];

  if (points.length <= 80) {
    const data = points.map((p) => ({
      label: formatTimestampLabel(p.ms),
      value: round(p.count),
      close: p.close,
      medium: p.medium,
      distant: p.distant,
    }));
    return [
      {
        id: "bluetooth-device-count",
        type: "area",
        title: "Bluetooth device count over time (UTC)",
        xAxisLabel: "Time (UTC)",
        yAxisLabel: "Device count",
        series: anyRssi ? rssiSeries : singleSeries,
        showDots: data.length <= 40 && !anyRssi,
        rotateLabels: true,
        data,
      },
    ];
  }

  const buckets = new Map<
    string,
    { label: string; sum: number; closeSum: number; mediumSum: number; distantSum: number; count: number }
  >();
  for (const p of points) {
    const { key, label } = timeBucket(new Date(p.ms), hourly);
    const b = buckets.get(key) ?? { label, sum: 0, closeSum: 0, mediumSum: 0, distantSum: 0, count: 0 };
    b.sum += p.count;
    b.closeSum += p.close;
    b.mediumSum += p.medium;
    b.distantSum += p.distant;
    b.count += 1;
    buckets.set(key, b);
  }
  const data = [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => ({
      label: v.label,
      value: round(v.sum / v.count),
      close: round(v.closeSum / v.count),
      medium: round(v.mediumSum / v.count),
      distant: round(v.distantSum / v.count),
    }));

  return [
    {
      id: "bluetooth-device-count",
      type: "area",
      title: `Bluetooth device count (${hourly ? "hourly" : "daily"} avg, UTC)`,
      xAxisLabel: "Time (UTC)",
      yAxisLabel: "Device count",
      series: anyRssi ? rssiSeries : singleSeries,
      rotateLabels: true,
      data,
    },
  ];
}

/**
 * Call Log (`CALL_LOG`): show duration on y-axis and time on x-axis.
 * Renders discrete call bars color-coded by call direction/type instead of a continuous spline.
 */
function callLogCharts(records: SensorRecord[]): ChartSpec[] {
  const points: { ms: number; duration: number; callType: string }[] = [];

  for (const r of records) {
    const duration = parseNum(r.values?.duration);
    const ms = recordTimestampMs(r, ["time_ms", "time", "timestamp"]);
    if (duration === null || ms === null) continue;
    const rawType = String(r.values?.type ?? "").trim().toUpperCase();
    const callType = rawType || "UNKNOWN";
    points.push({ ms, duration, callType });
  }

  if (points.length > 0) {
    points.sort((a, b) => a.ms - b.ms);
    const hasKnownTypes = points.some((p) => p.callType !== "UNKNOWN");

    const data = points.map((p) => {
      const dur = round(p.duration);
      const isIncoming = p.callType === "INCOMING";
      const isOutgoing = p.callType === "OUTGOING";
      return {
        label: formatTimestampLabel(p.ms),
        value: dur,
        incoming: isIncoming ? dur : 0,
        outgoing: isOutgoing ? dur : 0,
        other: !isIncoming && !isOutgoing ? dur : 0,
        callType: p.callType,
      };
    });

    const out: ChartSpec[] = [
      {
        id: "call-log-duration-over-time",
        type: "bar",
        title: "Call duration over time (seconds, UTC)",
        xAxisLabel: "Time (UTC)",
        yAxisLabel: "Duration (s)",
        valueFormat: "seconds",
        rotateLabels: true,
        series: hasKnownTypes
          ? [
              { key: "incoming", label: "Incoming", color: "#10B981", stackId: "call" },
              { key: "outgoing", label: "Outgoing", color: "#2563EB", stackId: "call" },
              { key: "other", label: "Missed / Other", color: "#F59E0B", stackId: "call" },
            ]
          : [{ key: "value", label: "Duration (s)", color: "#2563EB" }],
        tooltipMeta: hasKnownTypes ? [{ key: "callType", label: "Call type" }] : undefined,
        data,
      },
    ];

    const byType = categoryCounts(records, "type", "Calls by type");
    if (byType) {
      out.push({
        ...byType,
        series: [{ key: "value", label: "Calls", color: "#0D9488" }],
      });
    }

    return out;
  }

  const fallback = multiField(records, [
    { key: "incoming_calls", label: "Incoming" },
    { key: "outgoing_calls", label: "Outgoing" },
    { key: "missed_calls", label: "Missed" },
  ], "Calls");
  return fallback ? [fallback] : [];
}

/**
 * Sleep (`SLEEP`, `HEALTH_CONNECT`, `SLEEP_SESSION`): show sleep duration.
 * Supports both:
 * 1. Session durations from `HEALTH_CONNECT` `sleep_sessions` (or `SLEEP_SESSION` `duration_hours`)
 * 2. Estimated daily sleep hours from Google Sleep API `SLEEP` confidence samples
 *    anchored to a noon-to-noon sleep night so overnight sessions aren't split at 00:00 UTC.
 */
function sleepCharts(records: SensorRecord[]): ChartSpec[] {
  const charts: ChartSpec[] = [];
  // Shift by -6h when bucketing overnight samples so a single night of sleep
  // stays together instead of splitting at 00:00 UTC.
  const SLEEP_NIGHT_OFFSET_MS = 6 * 3_600_000;

  // 1. Explicit sleep sessions (HEALTH_CONNECT sleep_sessions or SLEEP_SESSION duration_hours)
  const sessionByDay = new Map<string, { label: string; userHours: Map<string, number> }>();
  const seenSessions = new Set<string>();

  for (const r of records) {
    const pid = r.participant_id || "default";
    const sessions = parseJsonArray(r.values?.sleep_sessions);
    if (sessions && sessions.length > 0) {
      for (const s of sessions) {
        if (!s || typeof s !== "object") continue;
        const obj = s as Record<string, unknown>;
        const startStr = String(obj.start_time ?? "");
        const endStr = String(obj.end_time ?? "");
        const startMs = Date.parse(startStr);
        const endMs = Date.parse(endStr);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;

        const dedupKey = `${pid}|${startStr}|${endStr}`;
        if (seenSessions.has(dedupKey)) continue;
        seenSessions.add(dedupKey);

        const hours = (endMs - startMs) / 3_600_000;
        const endDate = new Date(endMs);
        const { key, label } = timeBucket(endDate, false);
        const bucket = sessionByDay.get(key) ?? { label, userHours: new Map<string, number>() };
        bucket.userHours.set(pid, (bucket.userHours.get(pid) ?? 0) + hours);
        sessionByDay.set(key, bucket);
      }
      continue;
    }

    const directHours =
      parseNum(r.values?.duration_hours) ??
      (parseNum(r.values?.duration_minutes) !== null
        ? (parseNum(r.values?.duration_minutes) as number) / 60
        : null) ??
      (parseNum(r.values?.duration_seconds) !== null
        ? (parseNum(r.values?.duration_seconds) as number) / 3600
        : null);

    if (directHours !== null) {
      const ms = recordTimestampMs(r, ["end_time", "timestamp", "time"]);
      if (ms !== null) {
        const { key, label } = timeBucket(new Date(ms), false);
        const bucket = sessionByDay.get(key) ?? { label, userHours: new Map<string, number>() };
        bucket.userHours.set(pid, (bucket.userHours.get(pid) ?? 0) + directHours);
        sessionByDay.set(key, bucket);
      }
    }
  }

  // 2. SLEEP confidence samples (~10-minute Google Sleep API classify events)
  const sleepSamplesByUser = new Map<string, { ms: number; confidence: number }[]>();
  for (const r of records) {
    if (r.source.toUpperCase() !== "SLEEP") continue;
    const confidence = parseNum(r.values?.confidence);
    const ms = recordTimestampMs(r, ["timestamp", "time"]);
    if (confidence === null || ms === null) continue;
    const pid = r.participant_id || "default";
    const list = sleepSamplesByUser.get(pid) ?? [];
    list.push({ ms, confidence });
    sleepSamplesByUser.set(pid, list);
  }

  const classifyByDay = new Map<string, { label: string; userHours: Map<string, number> }>();
  if (sleepSamplesByUser.size > 0) {
    let anyHighConfidence = false;
    for (const list of sleepSamplesByUser.values()) {
      if (list.some((s) => s.confidence >= 50)) {
        anyHighConfidence = true;
        break;
      }
    }

    const DEFAULT_SAMPLE_HOURS = 10 / 60;
    const MAX_GAP_MS = 30 * 60 * 1000;

    for (const [pid, list] of sleepSamplesByUser.entries()) {
      list.sort((a, b) => a.ms - b.ms);
      for (let i = 0; i < list.length; i++) {
        const cur = list[i];
        const next = list[i + 1];
        const gapMs = next ? next.ms - cur.ms : 0;
        const intervalHours =
          gapMs > 0 && gapMs <= MAX_GAP_MS ? gapMs / 3_600_000 : DEFAULT_SAMPLE_HOURS;

        const sleepHours = anyHighConfidence
          ? cur.confidence >= 50
            ? intervalHours
            : 0
          : intervalHours * Math.max(0, Math.min(100, cur.confidence)) / 100;

        const { key, label } = timeBucket(new Date(cur.ms - SLEEP_NIGHT_OFFSET_MS), false);
        const bucket = classifyByDay.get(key) ?? { label, userHours: new Map<string, number>() };
        bucket.userHours.set(pid, Math.min(24, (bucket.userHours.get(pid) ?? 0) + sleepHours));
        classifyByDay.set(key, bucket);
      }
    }
  }

  const sleepTargetLine: ChartReferenceLine[] = [
    { y: 8, label: "8h target", color: "#10B981" },
  ];

  if (sessionByDay.size > 0) {
    const data = [...sessionByDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, bucket]) => {
        const vals = [...bucket.userHours.values()];
        const avg = vals.reduce((acc, v) => acc + v, 0) / vals.length;
        return { label: bucket.label, value: round(avg) };
      });

    charts.push({
      id: "sleep-session-duration",
      type: "bar",
      title: classifyByDay.size > 0 ? "Sleep duration (Health Connect sessions, hours)" : "Sleep duration (hours)",
      xAxisLabel: "Date (UTC)",
      yAxisLabel: "Hours",
      valueFormat: "hours",
      series: [{ key: "value", label: "Sleep (h)", color: "#6366F1" }],
      referenceLines: sleepTargetLine,
      data,
    });
  }

  if (classifyByDay.size > 0) {
    const data = [...classifyByDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, bucket]) => {
        const vals = [...bucket.userHours.values()];
        const avg = vals.reduce((acc, v) => acc + v, 0) / vals.length;
        return { label: bucket.label, value: round(avg) };
      });

    charts.push({
      id: "sleep-classify-duration",
      type: "bar",
      title: sessionByDay.size > 0 ? "Estimated sleep duration (Sleep API, hours)" : "Sleep duration (hours)",
      xAxisLabel: "Date (UTC)",
      yAxisLabel: "Hours",
      valueFormat: "hours",
      series: [{ key: "value", label: "Sleep (h)", color: "#6366F1" }],
      referenceLines: sleepTargetLine,
      data,
    });
  }

  return charts;
}

/**
 * Survey (`survey`): show survey submission times.
 * 1. Submission time-of-day (hour of day on y-axis vs submission date on x-axis, plotted as discrete dots)
 * 2. Complete 24-hour distribution of submissions by hour of day (00:00–23:00 UTC)
 */
function surveyCharts(records: SensorRecord[]): ChartSpec[] {
  const submissions: { ms: number; date: Date }[] = [];

  for (const r of records) {
    const ms = recordTimestampMs(r, ["completed_at", "timestamp", "time"]);
    if (ms === null) continue;
    submissions.push({ ms, date: new Date(ms) });
  }

  if (submissions.length === 0) return [];
  submissions.sort((a, b) => a.ms - b.ms);

  // 1. Time-of-day (0–24h) on y-axis vs submission date on x-axis
  const timeOfDayData = submissions.map(({ date }) => {
    const M = date.getUTCMonth() + 1;
    const D = date.getUTCDate();
    const H = date.getUTCHours();
    const Min = date.getUTCMinutes();
    return {
      label: `${M}/${D} ${pad2(H)}:${pad2(Min)}`,
      value: round2(H + Min / 60),
    };
  });

  // 2. Full 24-hour distribution by hour of day (00:00 – 23:00 UTC)
  const hourCounts = new Map<number, number>();
  for (const { date } of submissions) {
    const h = date.getUTCHours();
    hourCounts.set(h, (hourCounts.get(h) ?? 0) + 1);
  }
  const byHourData = Array.from({ length: 24 }, (_, h) => ({
    label: `${pad2(h)}:00`,
    value: hourCounts.get(h) ?? 0,
  }));

  return [
    {
      id: "survey-submission-times",
      type: "line",
      title: "Survey submission times (hour of day, UTC)",
      xAxisLabel: "Submission date (UTC)",
      yAxisLabel: "Time of day (UTC)",
      yDomain: [0, 24],
      valueFormat: "hourOfDay",
      showDots: true,
      rotateLabels: true,
      series: [
        {
          key: "value",
          label: "Time of day (UTC)",
          color: "#7C3AED",
          strokeWidth: 0,
        },
      ],
      data: timeOfDayData,
    },
    {
      id: "survey-submissions-by-hour",
      type: "bar",
      title: "Survey submissions by hour of day (UTC)",
      xAxisLabel: "Hour of day (UTC)",
      yAxisLabel: "Submissions",
      rotateLabels: true,
      series: [{ key: "value", label: "Submissions", color: "#7C3AED" }],
      data: byHourData,
    },
  ];
}

// ── Generic & other sources ──────────────────────────────────────────────────

type SourcePlot =
  | { kind: "metricTime"; field: string; agg: "avg" | "sum"; title: string; transform?: (n: number) => number }
  | { kind: "category"; field: string; title: string }
  | { kind: "multiField"; fields: { key: string; label: string }[]; title: string }
  | { kind: "none" };

const toMinutes = (s: number) => s / 60;

const SOURCE_PLOTS: Record<string, SourcePlot> = {
  NOTIFICATION_COUNT: { kind: "metricTime", field: "active_count", agg: "avg", title: "Active notifications (avg)" },
  SCREEN_SESSION: { kind: "metricTime", field: "session_duration_seconds", agg: "sum", title: "Screen-on time (minutes)", transform: toMinutes },
  BATTERY_LEVEL: { kind: "metricTime", field: "battery_level", agg: "avg", title: "Battery level % (avg)" },
  CHARGING_EVENT: { kind: "metricTime", field: "battery_level", agg: "avg", title: "Battery level % (avg)" },
  STEP_COUNT: { kind: "metricTime", field: "steps", agg: "sum", title: "Steps" },
  LIGHT: { kind: "metricTime", field: "lux", agg: "avg", title: "Ambient light (lux, avg)" },
  WIFI_EVENT: { kind: "metricTime", field: "count", agg: "avg", title: "Nearby Wi-Fi access points (avg)" },
  SMS_COUNT: { kind: "multiField", title: "SMS", fields: [
    { key: "sent_sms", label: "Sent" },
    { key: "received_sms", label: "Received" },
  ] },
  SMS_LOG: { kind: "category", field: "type", title: "SMS by type" },
  PHYSICAL_ACTIVITY: { kind: "category", field: "activity", title: "Activity type" },
  SCREEN_EVENT: { kind: "category", field: "state", title: "Screen state" },
  NOTIFICATION_POSTED: { kind: "category", field: "app_name", title: "Notifications by app" },
  NOTIFICATION_REMOVED: { kind: "category", field: "reason", title: "Notification removal reason" },
  CALENDAR_EVENT: { kind: "category", field: "category", title: "Calendar events by category" },
  ALIVE: { kind: "none" },
  DAILY_SUMMARY: { kind: "none" },
};

function sourceCharts(records: SensorRecord[], source: string, hourly: boolean): ChartSpec[] {
  const normalized = source.trim().toUpperCase();

  switch (normalized) {
    case "AMBIENT_AUDIO":
    case "AUDIO_CLASSIFICATION":
      return ambientAudioCharts(records);
    case "APP_USAGE":
      return appUsageCharts(records);
    case "AT_LOCATION":
      return locationCharts(records);
    case "BLUETOOTH_SCAN":
      return bluetoothCharts(records, hourly);
    case "CALL_LOG":
      return callLogCharts(records);
    case "SLEEP":
    case "SLEEP_SESSION":
      return sleepCharts(records);
    case "HEALTH_CONNECT": {
      const sleep = sleepCharts(records);
      if (sleep.length > 0) return sleep;
      const g = genericChart(records, hourly);
      return g ? [g] : [];
    }
    case "SURVEY":
      return surveyCharts(records);
  }

  const cfg = SOURCE_PLOTS[normalized];
  if (cfg) {
    switch (cfg.kind) {
      case "metricTime": {
        const c = metricOverTime(records, cfg.field, cfg.agg, cfg.title, hourly, cfg.transform);
        return c ? [c] : [];
      }
      case "category": {
        const c = categoryCounts(records, cfg.field, cfg.title);
        return c ? [c] : [];
      }
      case "multiField": {
        const c = multiField(records, cfg.fields, cfg.title);
        return c ? [c] : [];
      }
      case "none":
        return [];
    }
  }

  const g = genericChart(records, hourly);
  return g ? [g] : [];
}

/** fallback for unknown sources: first numeric field as a trend, else first categorical */
function genericChart(records: SensorRecord[], hourly: boolean): ChartSpec | null {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const r of records) {
    for (const k of Object.keys(r.values ?? {})) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  for (const k of keys) {
    let numeric = 0;
    let total = 0;
    const distinct = new Set<string>();
    for (const r of records) {
      const v = r.values?.[k];
      if (v === null || v === undefined || v === "") continue;
      total += 1;
      distinct.add(String(v));
      if (parseNum(v) !== null) numeric += 1;
    }
    if (total === 0) continue;
    if (numeric / total >= 0.7 && distinct.size > 1) {
      const c = metricOverTime(records, k, "avg", `${k} (avg)`, hourly);
      if (c) return c;
    }
  }
  for (const k of keys) {
    const distinct = new Set<string>();
    for (const r of records) {
      const v = r.values?.[k];
      if (v !== null && v !== undefined && v !== "") distinct.add(String(v));
    }
    if (distinct.size >= 2 && distinct.size <= MAX_CATEGORIES) {
      const c = categoryCounts(records, k, k);
      if (c) return c;
    }
  }
  return null;
}

/**
 * Build the chart specs for the visualization dialog and visualization page.
 * `source` "" means all sources are selected. `window` is the active filter
 * range and drives the time-bucket granularity (hourly for <=48h, else daily).
 */
export function buildCharts(records: SensorRecord[], source: string, window?: TimeWindow): ChartSpec[] {
  if (records.length === 0) return [];
  const hourly = isHourly(records, window);

  if (!source) {
    const counts = new Map<string, number>();
    for (const r of records) counts.set(r.source, (counts.get(r.source) ?? 0) + 1);
    const data = [...counts.entries()]
      .sort(([, a], [, b]) => b - a)
      .slice(0, MAX_CATEGORIES)
      .map(([label, value]) => ({ label, value }));
    return [
      recordsOverTime(records, hourly, window),
      {
        id: "by-source",
        type: "horizontal-bar",
        title: "Records by source",
        xAxisLabel: "Records",
        yAxisLabel: "Source",
        series: [{ key: "value", label: "Records", color: "#2563EB" }],
        data,
      },
    ];
  }

  // When SLEEP was requested and we also fetched HEALTH_CONNECT fallback records,
  // compute recordsOverTime from the primary SLEEP records when present.
  const normalizedSource = source.trim().toUpperCase();
  const primaryRecords =
    normalizedSource === "SLEEP"
      ? (() => {
          const sleepOnly = records.filter((r) => r.source.toUpperCase() === "SLEEP");
          return sleepOnly.length > 0 ? sleepOnly : records;
        })()
      : records;

  const specific = sourceCharts(records, source, hourly);
  return [...specific, recordsOverTime(primaryRecords, hourly, window)];
}
